import AppstoreFilled from "@ant-design/icons/AppstoreFilled";
import { ChevronRight, Layers } from "lucide-react";
import Title from "common/Typography/Title.tsx";
import Select from "common/Select/Select.tsx";
import React from "react";
import styled, { useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import Text from "common/Typography/Text.tsx";
import useWorkspace from "utils/hooks/useWorkspace.ts";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import { WORKSPACE_TO_DISPLAY_NAME } from "@shared/features/workspaces/constants.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

interface WorkspaceSelectorProps {
  workspace: WorkspaceType | undefined;
  /** New look phone menu: a 48px row (icon, name, chevron) with no label. */
  row?: boolean;
}

const WorkspaceSelector = ({ workspace, row }: WorkspaceSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { onWorkspaceChange, isWorkspaceValid } = useWorkspace();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();

  const getValue = () => {
    if (workspace === WorkspaceType.DEMO) return WorkspaceType.USER;
    return workspace;
  };

  const getIsDisabled = (workspace: WorkspaceType) => {
    if (workspace === WorkspaceType.USER) {
      return (
        !isWorkspaceValid(WorkspaceType.USER) &&
        !isWorkspaceValid(WorkspaceType.DEMO)
      );
    } else {
      return !isWorkspaceValid(workspace);
    }
  };

  const onChange = (value: WorkspaceType) => {
    if (
      (value === WorkspaceType.USER && workspace === WorkspaceType.ADMIN) ||
      value === WorkspaceType.ADMIN
    ) {
      onWorkspaceChange(value as WorkspaceType);
    }
  };

  const options = [WorkspaceType.ADMIN, WorkspaceType.USER].map((workspace) => {
    const isDisabled = getIsDisabled(workspace);
    return {
      value: workspace,
      label: (
        <StyledText $isDisabled={isDisabled}>
          {t(
            WORKSPACE_TO_DISPLAY_NAME[
              workspace
            ] as TranslationKeys<LanguageNameSpaces.COMMON>
          )}
        </StyledText>
      ),
    };
  });

  if (isNewLook && row)
    return (
      <RowSelect
        value={getValue()}
        onChange={(value) => onChange(value as WorkspaceType)}
        options={options}
        mobileTitle={t("workspace.workspaces")}
        variant="borderless"
        prefix={<Layers size={20} strokeWidth={1.75} />}
        suffixIcon={<ChevronRight size={16} strokeWidth={1.75} />}
        aria-label={t("workspace.workspaces")}
      />
    );

  if (isNewLook)
    return (
      <>
        <NewLookLabel>{t("workspace.workspaces")}</NewLookLabel>
        <NewLookSelect
          value={getValue()}
          onChange={(value) => onChange(value as WorkspaceType)}
          options={options}
          mobileTitle={t("workspace.workspaces")}
        />
      </>
    );

  return (
    <>
      <WorkspaceTitleWrapper>
        <AppstoreFilled
          style={{
            marginRight: isBottomNavigator ? 6 : 10,
            fontSize: isBottomNavigator ? 12 : 16,
          }}
        />
        <Title level={isBottomNavigator ? 5 : 4} type="secondary">
          {t("workspace.workspaces").toUpperCase()}
        </Title>
      </WorkspaceTitleWrapper>
      <StyledSelect
        value={getValue()}
        onChange={(value) => onChange(value as WorkspaceType)}
        rounded={true}
        bordered={false}
        style={{ marginTop: isBottomNavigator ? 8 : undefined }}
        options={options}
        mobileTitle={t("workspace.workspaces")}
      />
    </>
  );
};

export default WorkspaceSelector;

const WorkspaceTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.colors.secondaryGrey};
`;
const StyledSelect = styled(Select)`
  width: 100%;
  margin: 16px 0;
  .ant-select-selector {
    background: ${(p) => p.theme.colors.lightBlue}!important;
  }
`;
// New look: a small group label like the menu's, over a plain field.
const NewLookLabel = styled.div`
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  padding: 0 8px 8px;
`;
const NewLookSelect = styled(Select)`
  width: 100%;
  margin-bottom: 16px;
`;
const StyledText = styled(Text)<{ $isDisabled: boolean }>`
  ${(p) =>
    p.$isDisabled &&
    `
    color: ${p.theme.colors.secondaryGrey}!important;
    cursor: auto;
 `}
`;
const RowSelect = styled(Select)`
  width: 100%;
  height: 44px;
  &.ant-select {
    padding: 0 !important;
  }
  .ant-select-prefix {
    margin-inline-end: 12px;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
  .ant-select-content-value,
  .ant-select-selection-item {
    font-size: 14px;
  }
  .ant-select-suffix {
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
`;
